/**
 * Grid Layout Switcher
 * Handles switching between single and double column layouts
 * Only active on devices under 989px (mobile/tablet)
 */

class GridLayoutSwitcher {
  constructor() {
    this.productGrid = null;
    this.buttons = [];
    this.currentLayout = 'double'; // default layout
    this.originalGridClasses = null;
    this.isActive = false;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  // Check if we're on mobile/tablet (under 989px)
  isMobileTablet() {
    return window.innerWidth < 989;
  }

  setupEventListeners() {
    // Find grid layout buttons
    this.buttons = document.querySelectorAll('[data-grid-layout]');
    
    // Find product grid
    this.productGrid = document.querySelector('#product-grid, .product-grid');
    
    if (!this.productGrid || this.buttons.length === 0) {
      console.warn('Grid layout switcher: Product grid or buttons not found');
      return;
    }

    // Add click event listeners to buttons
    this.buttons.forEach(button => {
      button.addEventListener('click', (e) => this.handleLayoutChange(e));
    });

    // Add window resize listener to handle screen size changes
    window.addEventListener('resize', () => this.handleResize());

    // Initial setup based on current screen size
    this.handleResize();

    // Handle dynamic content loading (for Load More functionality)
    this.observeGridChanges();
  }

  handleResize() {
    const shouldBeActive = this.isMobileTablet();
    
    if (shouldBeActive && !this.isActive) {
      // Activate mobile/tablet mode
      this.isActive = true;
      this.setLayout(this.currentLayout);
      this.updateButtonStates();
      console.log('Grid layout switcher: Activated for mobile/tablet');
    } else if (!shouldBeActive && this.isActive) {
      // Deactivate for desktop - remove custom classes
      this.isActive = false;
      this.removeGridLayoutClasses();
      console.log('Grid layout switcher: Deactivated for desktop');
    }
  }

  removeGridLayoutClasses() {
    if (this.productGrid) {
      this.productGrid.classList.remove('product-grid--single-column', 'product-grid--double-column');
    }
  }

  handleLayoutChange(event) {
    // Only handle clicks if we're in mobile/tablet mode
    if (!this.isActive) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const newLayout = button.dataset.gridLayout;
    
    if (newLayout && newLayout !== this.currentLayout) {
      this.setLayout(newLayout);
      this.updateButtonStates();
    }
  }

  setLayout(layout) {
    if (!this.productGrid || !this.isActive) {
      return;
    }

    // Remove existing layout classes
    this.productGrid.classList.remove('product-grid--single-column', 'product-grid--double-column');
    
    // Store original grid classes for restoration
    if (!this.originalGridClasses) {
      this.originalGridClasses = Array.from(this.productGrid.classList).filter(cls => 
        cls.includes('grid--') && !cls.includes('product-grid--')
      );
    }
    
    // Add new layout class
    if (layout === 'single') {
      this.productGrid.classList.add('product-grid--single-column');
      this.currentLayout = 'single';
    } else {
      this.productGrid.classList.add('product-grid--double-column');
      this.currentLayout = 'double';
    }

    // Store preference in localStorage
    try {
      localStorage.setItem('grid-layout-preference', this.currentLayout);
    } catch (e) {
      // Silently fail if localStorage is not available
    }

    // Trigger a custom event for other scripts to listen to
    this.productGrid.dispatchEvent(new CustomEvent('gridLayoutChanged', {
      detail: { 
        layout: this.currentLayout,
        previousLayout: layout === 'single' ? 'double' : 'single',
        isActive: this.isActive
      }
    }));

    console.log(`Grid layout changed to: ${this.currentLayout} (Mobile/Tablet mode)`);
  }

  updateButtonStates() {
    this.buttons.forEach(button => {
      const layout = button.dataset.gridLayout;
      
      if (layout === this.currentLayout) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  observeGridChanges() {
    // Watch for changes to the grid (like Load More adding new items)
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Re-apply current layout when new items are added (only if active)
          if (this.isActive) {
            this.setLayout(this.currentLayout);
          }
        }
      });
    });

    if (this.productGrid) {
      observer.observe(this.productGrid, {
        childList: true,
        subtree: true
      });
    }
  }

  // Method to load saved preference (only applies to mobile/tablet)
  loadSavedPreference() {
    if (!this.isActive) return;

    try {
      const savedLayout = localStorage.getItem('grid-layout-preference');
      if (savedLayout && (savedLayout === 'single' || savedLayout === 'double')) {
        this.setLayout(savedLayout);
        this.updateButtonStates();
      }
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }

  // Public method to get current layout
  getCurrentLayout() {
    return this.currentLayout;
  }

  // Public method to check if switcher is active
  isActivated() {
    return this.isActive;
  }

  // Public method to set layout programmatically (only works on mobile/tablet)
  switchTo(layout) {
    if (!this.isActive) {
      console.warn('Grid layout switcher: Not available on desktop');
      return;
    }

    if (layout === 'single' || layout === 'double') {
      this.setLayout(layout);
      this.updateButtonStates();
    }
  }
}

// Initialize the grid layout switcher
let gridLayoutSwitcher;

// Initialize when DOM is ready
function initGridLayoutSwitcher() {
  gridLayoutSwitcher = new GridLayoutSwitcher();
  
  // Load saved preference after initialization (with a slight delay)
  setTimeout(() => {
    gridLayoutSwitcher.loadSavedPreference();
  }, 100);
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGridLayoutSwitcher);
} else {
  initGridLayoutSwitcher();
}

// Re-initialize when sections are loaded (for Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  setTimeout(initGridLayoutSwitcher, 100);
});

// Export for global access
window.gridLayoutSwitcher = gridLayoutSwitcher; 